import _ from 'underscore';
import PropTypes from 'prop-types';
import React from 'react';
import classnames from 'classnames';
import {
  Form, Label, Button, Select,
} from '@bingads-webui-react/primitive';
import { ErrorMessages } from '@bingads-webui-react/validation';
import { ValidationStringMap } from '@bingads-webui/validation-string-map';
import { buildLegacyI18n } from '@bingads-webui/build-legacy-i18n';
import { Input, ValidationGroup } from '@bingads-webui-react/primitive-validation';
import { HelpIcon } from '@bingads-webui-campaign-react/help';
import { IF_FUNCTION_TYPE } from '../if-function-type';
import { AudienceSelector } from './audience-selector';

const IFF_ENTITY_SCHEMA = {
  name: 'IFF',
  type: 'object',
  properties: {
    insertText: {
      type: 'string',
      required: true,
    },
    defaultText: {
      type: 'string',
      required: false,
    },
    audienceList: {
      type: 'array',
      items: {
        type: 'string',
      },
    },
  },
};

const initialInputText = (autoInsertedText, fieldText) =>
  (autoInsertedText === fieldText ? undefined : fieldText);

/* eslint-disable react/prefer-stateless-function */
export class IffEditor extends React.Component {
  static propTypes = {
    IsNewAudienceEnabled: PropTypes.bool,
    audienceTargetsView: PropTypes.shape({
      render: PropTypes.func.isRequired,
    }).isRequired,
    audienceList: PropTypes.arrayOf(PropTypes.string),
    defaultText: PropTypes.string,
    i18n: PropTypes.shape({
      getString: PropTypes.func.isRequired,
    }).isRequired,
    insertText: PropTypes.string,
    type: PropTypes.string,
    onSave: PropTypes.func,
    onCancel: PropTypes.func,
  };
  static defaultProps = {
    IsNewAudienceEnabled: false,
    audienceList: [],
    defaultText: undefined,
    insertText: undefined,
    type: undefined,
    onSave: _.noop,
    onCancel: _.noop,
  };

  constructor(props) {
    super(props);

    const {
      i18n,
      defaultText,
      insertText,
      type,
    } = props;

    this.conditionTypeLabel = i18n.getString(_TL_('If'));
    this.conditionTypeHelpId = 'pop_BA_NewAdCreate_IFfunction_IF';
    this.conditionDeviceOptionText = i18n.getString(_TL_('Device'));
    this.conditionDeviceIsMobileStaticText = i18n.getString(_TL_('is mobile'));
    this.conditionAudienceOptionText = i18n.getString(_TL_('Audience'));
    this.conditionAudienceInStaticText = i18n.getString(_TL_('is one of the selected audiences below:'));
    this.insertTextLabel = i18n.getString(_TL_('Then insert'));
    this.insertTextHelpId = 'pop_BA_NewAdCreate_IFfunction_Then';
    this.insertTextPlaceholder = i18n.getString(_TL_('Text'));
    this.defaultTextLabel = i18n.getString(_TL_('Otherwise (optional)'));
    this.defaultTextHelpId = 'pop_BA_NewAdCreate_IFfunction_Optional';
    this.defaultTextPlaceholder = i18n.getString(_TL_('Default text'));
    this.syntaxInsertText = i18n.getString(_TL_('insert text'));
    this.syntaxDefaultText = i18n.getString(_TL_('default text'));
    this.saveButtonText = i18n.getString(_TL_('Apply'));
    this.cancelButtonText = i18n.getString(_TL_('Cancel'));

    this.errorNoAudiencesSelected = i18n.getString(_TL_('Select at least one audience'));

    this.onAudienceListChanged = this.onAudienceListChanged.bind(this);
    this.onConditionTypeChange = this.onConditionTypeChange.bind(this);

    this.onCancelClicked = this.onCancelClicked.bind(this);
    this.onSaveClicked = this.onSaveClicked.bind(this);

    this.state = {
      isValid: false,
      type,
      data: {
        defaultText: initialInputText(this.syntaxDefaultText, defaultText),
        insertText: initialInputText(this.syntaxInsertText, insertText),
      },
      audienceErrorMessages: [],
      selectedAudiences: [],
    };
  }

  onAudienceListChanged(selectedAudiences, isInitialLoad) {
    if (!isInitialLoad && _.isEmpty(selectedAudiences)) {
      this.setState({
        selectedAudiences,
        audienceErrorMessages: [this.errorNoAudiencesSelected],
      });
    } else {
      this.setState({
        selectedAudiences,
        audienceErrorMessages: [],
      });
    }
  }

  onConditionTypeChange(event) {
    this.setState({ type: event.target.value });
  }

  onSaveClicked(event) {
    const {
      type,
      data: {
        defaultText,
        insertText,
      },
    } = this.state;

    this.props.onSave(_.extend({
      event,
      defaultText,
      insertText,
      type,
    }, (type === IF_FUNCTION_TYPE.AUDIENCE_IN ? { audienceList: this.getAudienceList() } : {})));
  }

  onCancelClicked(event) {
    this.props.onCancel(event);
  }

  onValidate = (result) => {
    this.setState({
      isValid: result.isValid,
      data: result.data,
    });
  };

  getAudienceList() {
    return this.audienceSelector.getAudienceList();
  }

  render() {
    const { i18n, audienceTargetsView, audienceList } = this.props;
    const {
      type,
      data,
      isValid: areBaseInputsValid,
      selectedAudiences,
    } = this.state;

    const actualType = type === IF_FUNCTION_TYPE.AUDIENCE_IN ? type :
      IF_FUNCTION_TYPE.DEVICE_MOBILE;

    let isModeInputsValid = true;
    if (actualType === IF_FUNCTION_TYPE.AUDIENCE_IN) {
      isModeInputsValid = !_.isEmpty(selectedAudiences);
    }

    const isValid = areBaseInputsValid && isModeInputsValid;

    const audienceSelectorClass = this.props.IsNewAudienceEnabled ? 'iff-audience-selector--new' : 'iff-audience-selector--old';
    const legacyi18n = buildLegacyI18n(i18n, [ValidationStringMap]);

    return (
      <Form horizontal className={classnames('iff-editor', 'iff-editor__form-grid')}>
        <ValidationGroup validationProps={{
          schema: IFF_ENTITY_SCHEMA,
          i18n: legacyi18n,
          data,
          onValidate: this.onValidate,
        }}
        >
          <div className="iff-editor__label">
            <Label htmlFor="condition-type">{this.conditionTypeLabel}</Label>
            <HelpIcon
              topic={this.conditionTypeHelpId}
              ariaLabel={i18n.getString('Click to learn more about {{item}}', { item: this.conditionTypeLabel })}
            />
          </div>
          <div className="iff-editor__field">
            <Select id="condition-type" value={actualType} onChange={this.onConditionTypeChange} className="iff-editor__condition-type">
              <option value={IF_FUNCTION_TYPE.DEVICE_MOBILE}>
                {this.conditionDeviceOptionText}
              </option>
              <option value={IF_FUNCTION_TYPE.AUDIENCE_IN}>
                {this.conditionAudienceOptionText}
              </option>
            </Select>
            <span className="iff-editor__condition-type-text">
              {actualType === IF_FUNCTION_TYPE.AUDIENCE_IN ?
                this.conditionAudienceInStaticText :
                this.conditionDeviceIsMobileStaticText}
            </span>
          </div>

          {actualType === IF_FUNCTION_TYPE.AUDIENCE_IN ?
            <div className="iff-editor__field--audience-selector">

              <AudienceSelector
                className={classnames('iff-audience-selector', audienceSelectorClass)}
                audienceList={audienceList}
                audienceTargetsView={audienceTargetsView}
                onAudienceListChanged={this.onAudienceListChanged}
                ref={(component) => { this.audienceSelector = component; }}
              />
              <ErrorMessages messages={this.state.audienceErrorMessages} />
            </div> : null}

          <div className="iff-editor__label">
            <Label htmlFor="insert-text">{this.insertTextLabel}</Label>
            <HelpIcon
              topic={this.insertTextHelpId}
              ariaLabel={i18n.getString('Click to learn more about {{item}}', { item: this.IffEditor_InsertText_Label })}
            />
          </div>
          <div className="iff-editor__field">
            <Input
              id="insert-text"
              type="text"
              placeholder={this.insertTextPlaceholder}
              validationProps={{ field: 'insertText' }}
            />
          </div>

          <div className="iff-editor__label">
            <Label htmlFor="default-text">{this.defaultTextLabel}</Label>
            <HelpIcon
              topic={this.defaultTextHelpId}
              ariaLabel={i18n.getString('Click to learn more about {{item}}', { item: this.defaultTextPlaceholder })}
            />
          </div>
          <div className="iff-editor__field">
            <Input
              id="default-text"
              type="text"
              placeholder={this.defaultTextPlaceholder}
              validationProps={{ field: 'defaultText' }}
            />
          </div>
        </ValidationGroup>

        <div className="iff-editor__buttons-group">
          <Button
            bsStyle="primary"
            btnStyle="primary"
            onClick={this.onSaveClicked}
            disabled={!isValid}
          >
            {this.saveButtonText}
          </Button>
          <Button onClick={this.onCancelClicked}>{this.cancelButtonText}</Button>
        </div>
      </Form>
    );
  }
}
